import React, { useRef, useEffect, useState, useCallback, memo, createContext, useContext } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

// =========================================
// CHARACTER STAGGER TEXT
// Character-by-character reveal animation
// =========================================
export const CharacterStagger = memo(({ 
  text, 
  className,
  delay = 0,
  staggerDelay = 0.03,
  duration = 0.5,
  as: Component = 'span',
}) => {
  const characters = text.split('');

  return (
    <Component className={cn('inline-block', className)}>
      {characters.map((char, idx) => (
        <motion.span
          key={idx}
          initial={{ opacity: 0, y: 50, rotateX: -90 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{
            duration,
            delay: delay + idx * staggerDelay,
            ease: [0.215, 0.61, 0.355, 1],
          }}
          className="inline-block"
          style={{ 
            transformOrigin: 'bottom',
            whiteSpace: char === ' ' ? 'pre' : 'normal',
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </Component>
  );
});

CharacterStagger.displayName = 'CharacterStagger';

// =========================================
// TEXT GENERATE EFFECT
// Aceternity-style word-by-word fade in
// =========================================
export const TextGenerateEffect = memo(({ 
  words, 
  className,
  filter = true,
  duration = 0.5,
}) => {
  const wordsArray = words.split(' ');

  return (
    <div className={cn('font-bold', className)}>
      <div className="inline">
        {wordsArray.map((word, idx) => (
          <motion.span
            key={word + idx}
            initial={{ opacity: 0, filter: filter ? 'blur(10px)' : 'none' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{
              duration,
              delay: idx * 0.08,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="inline-block mr-2"
          >
            {word}
          </motion.span>
        ))}
      </div>
    </div>
  );
});

TextGenerateEffect.displayName = 'TextGenerateEffect';

// =========================================
// LAYERED PARALLAX BACKGROUND
// Multiple floating layers with depth
// =========================================
export const LayeredParallax = memo(({ children, className }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  return (
    <div ref={containerRef} className={cn('relative overflow-hidden', className)}>
      {/* Layer 1 - Furthest back, slowest */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          y: useTransform(scrollYProgress, [0, 1], [-50, 50]),
        }}
      >
        <div className="absolute top-[10%] left-[5%] w-96 h-96 rounded-full bg-teal/5 blur-3xl" />
        <div className="absolute bottom-[20%] right-[10%] w-64 h-64 rounded-full bg-orange/5 blur-2xl" />
      </motion.div>

      {/* Layer 2 - Middle layer */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          y: useTransform(scrollYProgress, [0, 1], [-100, 100]),
        }}
      >
        <div className="absolute top-[30%] right-[15%] w-32 h-32 border border-teal/20 rounded-full" />
        <div className="absolute bottom-[30%] left-[20%] w-48 h-48 border border-orange/10 rounded-full" />
        
        {/* Floating dots */}
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-[20%] left-[30%] w-3 h-3 bg-teal/30 rounded-full"
        />
        <motion.div
          animate={{ y: [0, 20, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute top-[60%] right-[25%] w-2 h-2 bg-orange/40 rounded-full"
        />
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          className="absolute bottom-[40%] left-[60%] w-4 h-4 bg-teal/20 rounded-full"
        />
      </motion.div>

      {/* Layer 3 - Closest, fastest parallax */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          y: useTransform(scrollYProgress, [0, 1], [-150, 150]),
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          className="absolute top-[15%] right-[5%] w-16 h-16 border border-dashed border-teal/20 rounded-lg"
        />
        <div className="absolute bottom-[10%] left-[10%]">
          <svg width="60" height="60" viewBox="0 0 60 60" className="opacity-20">
            <polygon points="30,5 55,50 5,50" fill="none" stroke="hsl(174, 100%, 27%)" strokeWidth="1" />
          </svg>
        </div>
      </motion.div>

      {/* Grid pattern overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(hsl(174, 100%, 27%) 1px, transparent 1px), linear-gradient(90deg, hsl(174, 100%, 27%) 1px, transparent 1px)`,
          backgroundSize: '50px 50px',
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
});

LayeredParallax.displayName = 'LayeredParallax';

// =========================================
// CURVED SECTION DIVIDER (Enhanced)
// Multiple wave patterns with animation
// =========================================
export const WaveDivider = memo(({ 
  className,
  fillColor = 'hsl(215, 50%, 6%)',
  waveColor = 'hsl(174, 100%, 27%)',
  flip = false,
  animated = true,
}) => {
  return (
    <div className={cn('w-full overflow-hidden', flip && 'rotate-180', className)}>
      <svg 
        viewBox="0 0 1440 200" 
        className="w-full h-auto relative"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={waveColor} stopOpacity="0.1" />
            <stop offset="50%" stopColor={waveColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={waveColor} stopOpacity="0.1" />
          </linearGradient>
        </defs>
        
        {/* Background wave */}
        <motion.path
          d="M0,100 C320,150 420,50 640,100 C860,150 960,50 1200,100 C1320,125 1400,75 1440,100 L1440,200 L0,200 Z"
          fill="url(#waveGradient)"
          initial={{ opacity: 0 }}
          animate={animated ? { opacity: 1 } : {}}
          transition={{ duration: 1 }}
        />
        
        {/* Middle wave */}
        <motion.path
          d="M0,120 C240,80 360,160 600,120 C840,80 960,160 1200,120 C1350,95 1420,145 1440,120 L1440,200 L0,200 Z"
          fill={fillColor}
          fillOpacity="0.5"
          initial={{ x: -20 }}
          animate={animated ? { x: 0 } : {}}
          transition={{ duration: 1.5, ease: 'easeOut' }}
        />
        
        {/* Front wave */}
        <path
          d="M0,140 C360,100 480,180 720,140 C960,100 1080,180 1320,140 C1400,125 1430,155 1440,140 L1440,200 L0,200 Z"
          fill={fillColor}
        />
      </svg>
    </div>
  );
});

WaveDivider.displayName = 'WaveDivider';

// =========================================
// HALFTONE PATTERN BACKGROUND
// Animated dot pattern
// =========================================
export const HalftonePattern = memo(({ 
  className,
  color = 'hsl(174, 100%, 27%)',
  animated = true,
}) => {
  const patternRef = useRef(null);
  
  useEffect(() => {
    if (!animated || !patternRef.current) return;
    
    gsap.to(patternRef.current, {
      backgroundPosition: '40px 40px',
      duration: 20,
      repeat: -1,
      ease: 'none',
    });
  }, [animated]);

  return (
    <div 
      ref={patternRef}
      className={cn('absolute inset-0 opacity-10 pointer-events-none', className)}
      style={{
        backgroundImage: `radial-gradient(circle, ${color} 1.5px, transparent 1.5px)`,
        backgroundSize: '20px 20px',
        backgroundPosition: '0 0',
      }}
    />
  );
});

HalftonePattern.displayName = 'HalftonePattern';

// =========================================
// APPLE CARDS CAROUSEL
// Premium carousel with expand animation
// =========================================
const CarouselContext = createContext({
  onCardClose: () => {},
  currentIndex: 0,
});

export const AppleCarousel = memo(({ items, initialScroll = 0 }) => {
  const carouselRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const checkScrollability = useCallback(() => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  }, []);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll, checkScrollability]);

  const scrollLeftFn = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRightFn = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const handleCardClose = (index) => {
    if (carouselRef.current) {
      const isMobile = window && window.innerWidth < 768;
      const cardWidth = isMobile ? 230 : 384;
      const gap = isMobile ? 16 : 24;
      const scrollPosition = (cardWidth + gap) * (index + 1);
      carouselRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
      setCurrentIndex(index);
    }
  };

  return (
    <CarouselContext.Provider value={{ onCardClose: handleCardClose, currentIndex }}>
      <div className="relative w-full">
        <div
          className="flex w-full overflow-x-scroll py-10 md:py-20 scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          ref={carouselRef}
          onScroll={checkScrollability}
        >
          <div className="flex flex-row justify-start gap-4 md:gap-6 pl-4 max-w-7xl mx-auto">
            {items.map((item, index) => (
              <motion.div
                key={`card-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.1 * index,
                  ease: 'easeOut',
                }}
                className="last:pr-[5%] md:last:pr-[33%] rounded-3xl"
              >
                {item}
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Navigation buttons */}
        <div className="flex justify-end gap-2 mr-10">
          <button
            className={cn(
              'relative z-40 h-10 w-10 rounded-full bg-glass-surface backdrop-blur-md flex items-center justify-center border border-glass-border transition-all',
              canScrollLeft ? 'opacity-100 hover:bg-glass-hover' : 'opacity-30 cursor-not-allowed'
            )}
            onClick={scrollLeftFn}
            disabled={!canScrollLeft}
          >
            <ChevronLeft className="h-5 w-5 text-foreground" />
          </button>
          <button
            className={cn(
              'relative z-40 h-10 w-10 rounded-full bg-glass-surface backdrop-blur-md flex items-center justify-center border border-glass-border transition-all',
              canScrollRight ? 'opacity-100 hover:bg-glass-hover' : 'opacity-30 cursor-not-allowed'
            )}
            onClick={scrollRightFn}
            disabled={!canScrollRight}
          >
            <ChevronRight className="h-5 w-5 text-foreground" />
          </button>
        </div>
      </div>
    </CarouselContext.Provider>
  );
});

AppleCarousel.displayName = 'AppleCarousel';

export const AppleCard = memo(({ 
  card, 
  index, 
  layout = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const { onCardClose } = useContext(CarouselContext);

  const handleClose = useCallback(() => {
    setOpen(false);
    onCardClose(index);
  }, [onCardClose, index]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [open, handleClose]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <div className="fixed inset-0 z-50 overflow-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-void/80 backdrop-blur-lg"
              onClick={handleClose}
            />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              ref={containerRef}
              layoutId={layout ? `card-${card.title}` : undefined}
              className="max-w-5xl mx-auto my-10 bg-void-light rounded-3xl border border-glass-border p-4 md:p-10 relative z-[60]"
            >
              <button
                className="sticky top-4 ml-auto flex h-8 w-8 items-center justify-center rounded-full bg-glass-surface hover:bg-glass-hover transition-colors"
                onClick={handleClose}
              >
                <X className="h-5 w-5 text-foreground" />
              </button>
              <motion.p
                layoutId={layout ? `category-${card.title}` : undefined}
                className="text-sm font-mono text-teal uppercase tracking-wider"
              >
                {card.category}
              </motion.p>
              <motion.h2
                layoutId={layout ? `title-${card.title}` : undefined}
                className="text-3xl md:text-5xl font-display font-bold text-foreground mt-4"
              >
                {card.title}
              </motion.h2>
              <div className="py-10">{card.content}</div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      
      <motion.button
        layoutId={layout ? `card-${card.title}` : undefined}
        onClick={() => setOpen(true)}
        className="rounded-3xl bg-glass-surface backdrop-blur-xl border border-glass-border h-80 w-56 md:h-[28rem] md:w-96 overflow-hidden flex flex-col items-start justify-start relative z-10 group"
        whileHover={{ scale: 1.02, y: -5 }}
        transition={{ duration: 0.3 }}
      >
        <div className={cn(
          "absolute inset-0 z-20",
          card.hideOverlayText
            ? "bg-gradient-to-b from-transparent via-transparent to-void/30"
            : "bg-gradient-to-b from-transparent via-void/50 to-void"
        )} />
        {!card.hideOverlayText && (
          <div className="relative z-30 p-6 md:p-8 h-full flex flex-col justify-end">
            <motion.p
              layoutId={layout ? `category-${card.title}` : undefined}
              className="text-xs md:text-sm font-mono text-teal uppercase tracking-wider mb-2"
            >
              {card.category}
            </motion.p>
            <motion.h3
              layoutId={layout ? `title-${card.title}` : undefined}
              className="text-xl md:text-2xl font-display font-bold text-foreground text-left"
            >
              {card.title}
            </motion.h3>
          </div>
        )}
        <BlurImage
          src={card.src}
          alt={card.title}
          className="object-cover absolute inset-0 z-10 group-hover:scale-105 transition-transform duration-500"
          style={card.objectPosition ? { objectPosition: card.objectPosition } : undefined}
        />
      </motion.button>
    </>
  );
});

AppleCard.displayName = 'AppleCard';

// Blur Image component for smooth loading
export const BlurImage = memo(({ src, alt, className, style }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <motion.img
      src={src}
      alt={alt}
      className={cn('w-full h-full transition-all duration-700', className)}
      style={style}
      initial={{ opacity: 0, filter: 'blur(20px)' }}
      animate={{
        opacity: isLoaded ? 1 : 0,
        filter: isLoaded ? 'blur(0px)' : 'blur(20px)'
      }}
      onLoad={() => setIsLoaded(true)}
    />
  );
});

BlurImage.displayName = 'BlurImage';

// =========================================
// INFINITE MOVING CARDS (Logo Carousel)
// =========================================
export const InfiniteMovingCards = memo(({
  items,
  direction = 'left',
  speed = 'slow',
  pauseOnHover = true,
  className,
}) => {
  const containerRef = useRef(null);
  const scrollerRef = useRef(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    const addAnimation = () => {
      if (containerRef.current && scrollerRef.current) {
        const scrollerContent = Array.from(scrollerRef.current.children);

        scrollerContent.forEach((item) => {
          const duplicatedItem = item.cloneNode(true);
          if (scrollerRef.current) {
            scrollerRef.current.appendChild(duplicatedItem);
          }
        });

        if (containerRef.current) {
          if (direction === 'left') {
            containerRef.current.style.setProperty('--animation-direction', 'forwards');
          } else {
            containerRef.current.style.setProperty('--animation-direction', 'reverse');
          }
        }

        if (containerRef.current) {
          if (speed === 'fast') {
            containerRef.current.style.setProperty('--animation-duration', '20s');
          } else if (speed === 'normal') {
            containerRef.current.style.setProperty('--animation-duration', '40s');
          } else {
            containerRef.current.style.setProperty('--animation-duration', '80s');
          }
        }

        setStart(true);
      }
    };

    addAnimation();
  }, [direction, speed]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'scroller relative z-20 max-w-7xl overflow-hidden [mask-image:linear-gradient(to_right,transparent,white_20%,white_80%,transparent)]',
        className
      )}
    >
      <ul
        ref={scrollerRef}
        className={cn(
          'flex min-w-full shrink-0 gap-8 py-4 w-max flex-nowrap',
          start && 'animate-scroll',
          pauseOnHover && 'hover:[animation-play-state:paused]'
        )}
      >
        {items.map((item, idx) => (
          <li
            className="relative flex-shrink-0"
            key={idx}
          >
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
});

InfiniteMovingCards.displayName = 'InfiniteMovingCards';

// =========================================
// SPARKLES CORE
// =========================================
export const SparklesCore = memo(({
  id,
  className,
  background,
  minSize = 0.4,
  maxSize = 1,
  particleDensity = 100,
  particleColor = '#00897B',
  particleSpeed = 1,
}) => {
  const canvasRef = useRef(null);
  const particles = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
      initParticles();
    };

    const initParticles = () => {
      particles.current = Array.from({ length: particleDensity }, () => ({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        size: Math.random() * (maxSize - minSize) + minSize,
        speedX: (Math.random() - 0.5) * particleSpeed,
        speedY: (Math.random() - 0.5) * particleSpeed,
        opacity: Math.random(),
        opacitySpeed: (Math.random() - 0.5) * 0.02,
      }));
    };

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      particles.current.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.opacity += p.opacitySpeed;

        if (p.opacity <= 0 || p.opacity >= 1) p.opacitySpeed *= -1;
        if (p.x < 0 || p.x > canvas.offsetWidth) p.speedX *= -1;
        if (p.y < 0 || p.y > canvas.offsetHeight) p.speedY *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = particleColor;
        ctx.globalAlpha = p.opacity;
        ctx.fill();
      });

      ctx.globalAlpha = 1;
      animationId = requestAnimationFrame(animate);
    };

    resize();
    window.addEventListener('resize', resize);
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [minSize, maxSize, particleDensity, particleColor, particleSpeed]);

  return (
    <canvas
      ref={canvasRef}
      id={id}
      className={cn('absolute inset-0', className)}
      style={{ background }}
    />
  );
});

SparklesCore.displayName = 'SparklesCore';

// =========================================
// SPOTLIGHT
// =========================================
export const Spotlight = memo(({ 
  className,
  fill = 'white',
}) => {
  return (
    <svg
      className={cn(
        'animate-spotlight pointer-events-none absolute z-[1] h-[169%] w-[138%] lg:w-[84%] opacity-0',
        className
      )}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 3787 2842"
      fill="none"
    >
      <g filter="url(#filter)">
        <ellipse
          cx="1924.71"
          cy="273.501"
          rx="1924.71"
          ry="273.501"
          transform="matrix(-0.822377 -0.568943 -0.568943 0.822377 3631.88 2291.09)"
          fill={fill}
          fillOpacity="0.21"
        />
      </g>
      <defs>
        <filter
          id="filter"
          x="0.860352"
          y="0.838989"
          width="3785.16"
          height="2840.26"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="151" result="effect1_foregroundBlur_1065_8" />
        </filter>
      </defs>
    </svg>
  );
});

Spotlight.displayName = 'Spotlight';

// =========================================
// PARALLAX IMAGE
// Single image with parallax effect
// =========================================
export const ParallaxImage = memo(({ 
  src, 
  alt, 
  className,
  speed = 0.3,
}) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], [speed * -100, speed * 100]);

  return (
    <div ref={ref} className={cn('overflow-hidden', className)}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-full object-cover scale-125"
      />
    </div>
  );
});

ParallaxImage.displayName = 'ParallaxImage';

// Export all components
export default {
  CharacterStagger,
  TextGenerateEffect,
  LayeredParallax,
  WaveDivider,
  HalftonePattern,
  AppleCarousel,
  AppleCard,
  BlurImage,
  InfiniteMovingCards,
  SparklesCore,
  Spotlight,
  ParallaxImage,
};
